import { isProdEnv } from '@/utils/isProdEnv'
import { isMartechSuppressed } from '@/utils/martech'
import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser'
import type { App, Plugin } from 'vue'
import { type AnalyticsClient, setCustomerIOClient } from './analyticsClients'

export type CustomerIOOptions = {
  writeKey: string
}

type LoadableAnalyticsClient = AnalyticsClient & {
  catch?: (onRejected: () => void) => unknown
}

const shouldEnableAnonymousInApp = (): boolean => {
  if (import.meta.env.DEV || import.meta.env.TEST || typeof location === 'undefined') {
    return false
  }

  return isProdEnv()
}

export const customerioPlugin: Plugin = {
  async install(_: App, { writeKey }: CustomerIOOptions): Promise<void> {
    setCustomerIOClient(undefined)

    if (!writeKey) return

    // Skip loading CIO on local dev — set VITE_MARTECH_ENABLED=true in .env to test locally.
    if (isMartechSuppressed()) return

    try {
      const instance = AnalyticsBrowser.load(
        {
          writeKey,
        },
        {
          user: {
            cookie: {
              /**
               * Customer.io and Segment use the same underlying analytics SDK and
               * default to the same localStorage/cookie key (`ajs_user_id`).
               * Namespace CIO separately so the two libraries don't clobber each
               * other — otherwise CIO can pick up Segment's stored user ID
               * instead of the email we identify with. Same config as www app.
               */
              key: 'cio_user_id',
            },
          },
          integrations: {
            // Anonymous in-app messages can interfere with local and E2E runs.
            'Customer.io In-App Plugin': {
              anonymousInApp: shouldEnableAnonymousInApp(),
            },
          },
        },
      ) as unknown as LoadableAnalyticsClient

      instance.catch?.(() => {
        console.error('Error loading Customer.io')
        setCustomerIOClient(undefined)
      })

      setCustomerIOClient(instance)
    } catch (err) {
      console.error('Error loading Customer.io: ', err)
    }
  },
}
